import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Empty, DataTag } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BellSimple, BellRinging, Trash, Lightning, MagnifyingGlass, Lightning as Bolt } from "@phosphor-icons/react";
import { toast } from "sonner";
import TelegramConnect from "@/components/TelegramConnect";

const TYPES = [
  ["price_above", "Price rises above"],
  ["price_below", "Price falls below"],
  ["score_above", "AI Score rises above"],
  ["score_below", "AI Score falls below"],
];

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({ ticker: "", alert_type: "price_above", threshold: "" });

  const load = () => api.get("/alerts").then((r) => setAlerts(r.data.alerts)).catch(() => {});
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [signals, setSignals] = useState(null);
  const [scanning, setScanning] = useState(false);
  const loadStrategies = () => api.get("/strategies").then((r) => setSavedStrategies(r.data.strategies)).catch(() => {});
  useEffect(() => { load(); loadStrategies(); }, []);

  const deleteStrategy = async (id) => { await api.delete(`/strategies/${id}`); loadStrategies(); };
  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/strategies/scan");
      setSignals(r.data.signals);
      r.data.signals.length > 0
        ? toast.success(`${r.data.signals.length} fresh signal(s)`)
        : toast(`Scanned ${r.data.scanned} watchlist names`, { description: "No fresh buy/sell signals today" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const [order, setOrder] = useState(null); // {ticker, side, price, strategy_name}
  const [qty, setQty] = useState(10);
  const [placing, setPlacing] = useState(false);
  const openOrder = (sig) => { setOrder({ ...sig, side: sig.signal }); setQty(10); };
  const placeOrder = async () => {
    setPlacing(true);
    try {
      await api.post("/orders", { ticker: order.ticker, side: order.side, quantity: Number(qty), order_type: "market" });
      toast.success(`Paper ${order.side} ${qty} ${order.ticker} placed`);
      setOrder(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPlacing(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/alerts", { ticker: form.ticker.toUpperCase(), alert_type: form.alert_type, threshold: Number(form.threshold) });
      setForm({ ...form, ticker: "", threshold: "" }); load(); toast.success("Alert created");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/alerts/${id}`); load(); };
  const check = async () => {
    const r = await api.post("/alerts/check");
    r.data.count > 0 ? toast.success(`${r.data.count} alert(s) triggered`) : toast("No alerts triggered", { description: "Conditions not met yet" });
    load();
  };
  const label = (t) => TYPES.find((x) => x[0] === t)?.[1] || t;

  return (
    <div>
      <div className="flex items-end justify-between">
        <div><div className="label-xs">User Intelligence</div><h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2"><BellSimple size={26} className="text-volt" /> Alerts</h1></div>
        <Button data-testid="check-alerts-btn" onClick={check} variant="outline" className="rounded-sm border-border gap-2"><BellRinging size={16} /> Evaluate Now</Button>
      </div>

      <TelegramConnect />

      <form onSubmit={create} className="mt-4 border border-border bg-card p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <div className="label-xs mb-2">Ticker</div>
          <Input data-testid="alert-ticker" value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value })} required placeholder="AAPL or BTC-USD" className="bg-background rounded-sm mono uppercase" />
          <div className="mono text-[10px] text-muted-foreground mt-1">Stocks & crypto (BTC-USD) · crypto checks 24/7</div>
        </div>
        <div>
          <div className="label-xs mb-2">Condition</div>
          <Select value={form.alert_type} onValueChange={(v) => setForm({ ...form, alert_type: v })}>
            <SelectTrigger data-testid="alert-type" className="bg-background rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-xs mb-2">Threshold</div>
          <Input data-testid="alert-threshold" type="number" step="any" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} required placeholder="200" className="bg-background rounded-sm mono" />
        </div>
        <Button data-testid="create-alert-btn" type="submit" className="bg-volt hover:bg-volt/90 rounded-sm h-10">Create Alert</Button>
      </form>

      <div className="mt-4 border border-border bg-card">
        <div className="border-b border-border px-4 py-3 label-xs flex items-center justify-between">
          <span>Active &amp; Triggered Alerts</span>
          <span className="normal-case text-[10px] text-muted-foreground/70" data-testid="alert-cooldown-note">Triggered alerts auto re-arm after 60 min</span>
        </div>
        {alerts.length === 0 ? <Empty>No alerts configured</Empty> : alerts.map((a) => (
          <div key={a.id} data-testid={`alert-${a.id}`} className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <div className="flex items-center gap-3">
              <span className="mono font-medium w-16">{a.ticker}</span>
              <span className="text-sm text-muted-foreground">{label(a.alert_type)} <span className="mono text-white">{a.threshold}</span></span>
            </div>
            <div className="flex items-center gap-4">
              <span className={`mono text-[11px] uppercase px-2 py-0.5 border rounded-sm ${a.status === "triggered" ? "text-signal-green border-signal-green/40 bg-signal-green/10" : "text-muted-foreground border-border"}`}>{a.status}</span>
              <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-signal-red transition-colors"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Strategy Alerts */}
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Lightning size={20} className="text-signal-yellow" /> Strategy Alerts</h2>
        <Button data-testid="scan-strategies-btn" onClick={scan} disabled={scanning} className="bg-volt hover:bg-volt/90 rounded-sm gap-2"><MagnifyingGlass size={16} /> {scanning ? "Scanning…" : "Scan Watchlist"}</Button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Save strategies from the Backtesting page, then scan your watchlist for names that fire a FRESH buy or sell signal today.</p>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3 label-xs">Saved Strategies</div>
          {savedStrategies.length === 0 ? <Empty>No saved strategies — build one on the Backtesting page</Empty> : savedStrategies.map((s) => (
            <div key={s.id} data-testid={`strategy-${s.id}`} className="flex items-center justify-between px-4 py-3 border-b border-border/50">
              <div><div className="font-medium text-sm">{s.name}</div><div className="mono text-[11px] text-muted-foreground uppercase">{s.strategy}</div></div>
              <button onClick={() => deleteStrategy(s.id)} className="text-muted-foreground hover:text-signal-red transition-colors"><Trash size={15} /></button>
            </div>
          ))}
        </div>

        <div className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3 label-xs">Fired Signals</div>
          {signals === null ? <Empty>Run a scan to check for fresh signals</Empty> :
            signals.length === 0 ? <Empty>No fresh buy/sell signals right now</Empty> :
            signals.map((sig, i) => (
              <div key={i} data-testid="fired-signal" className="flex items-center justify-between px-4 py-3 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <span className={`mono text-[11px] uppercase px-2 py-1 rounded-sm border ${sig.signal === "buy" ? "text-signal-green border-signal-green/40 bg-signal-green/10" : "text-signal-red border-signal-red/40 bg-signal-red/10"}`}>{sig.signal}</span>
                  <div><div className="mono font-medium">{sig.ticker}</div><div className="text-[11px] text-muted-foreground">{sig.strategy_name}</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="mono text-sm">${sig.price}</span>
                  <Button data-testid={`trade-signal-${sig.ticker}`} size="sm" onClick={() => openOrder(sig)}
                    className={`h-7 rounded-sm gap-1 text-xs ${sig.signal === "buy" ? "bg-signal-green hover:bg-signal-green/90 text-black" : "bg-signal-red hover:bg-signal-red/90 text-white"}`}>
                    <Bolt size={13} weight="fill" /> Trade
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* One-click pre-filled paper order */}
      <Dialog open={!!order} onOpenChange={(o) => !o && setOrder(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="signal-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Bolt size={18} weight="fill" className="text-volt" /> Act on Signal</DialogTitle>
          </DialogHeader>
          {order && (
            <div className="hypo-bg border border-white/10 p-4 rounded-sm">
              <div className="label-xs italic mb-3">Pre-filled paper order · simulated</div>
              <div className="flex items-center justify-between mb-3">
                <div><div className="mono text-lg font-semibold">{order.ticker}</div><div className="text-[11px] text-muted-foreground">{order.strategy_name}</div></div>
                <span className={`mono text-xs uppercase px-2 py-1 rounded-sm border ${order.side === "buy" ? "text-signal-green border-signal-green/40 bg-signal-green/10" : "text-signal-red border-signal-red/40 bg-signal-red/10"}`}>{order.side}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="label-xs mb-1">Quantity</div>
                  <Input data-testid="signal-order-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 bg-background rounded-sm mono" />
                </div>
                <div className="flex-1">
                  <div className="label-xs mb-1">Est. Value</div>
                  <div className="mono h-9 flex items-center">${(qty * order.price).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
              <div className="mono text-[11px] text-muted-foreground mt-2">Fills at ~${order.price} (market)</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrder(null)} className="rounded-sm border-border">Cancel</Button>
            <Button data-testid="confirm-signal-order" disabled={placing} onClick={placeOrder}
              className={`rounded-sm ${order?.side === "buy" ? "bg-signal-green hover:bg-signal-green/90 text-black" : "bg-signal-red hover:bg-signal-red/90 text-white"}`}>
              {placing ? "Placing…" : `Place ${order?.side} order`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
